import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

import { impact, notify } from '@/telegram/haptics';

export interface TimerOverlayProps {
  /** Сколько отсчитывать, секунд. Берётся из `card.duration`. */
  seconds: number;
  text: string;
  onClose: () => void;
}

/**
 * 05 · Реквизит, таймер. Не модалка, а затемнение поверх карты: контекст
 * задания не теряется, читать его продолжают по ходу отсчёта.
 *
 * Отсчёт локальный, и это не нарушение «правил на клиенте нет»: сервер про
 * секундомер ничего не знает и знать не должен — партию таймер не двигает,
 * ни одного намерения не отправляет и ни на что не влияет, кроме будильника
 * за столом. Соседний реквизит — монета и барабан имён — устроен иначе:
 * там бросок делает сервер, и до появления результата в контракте их нет.
 */
export function TimerOverlay({ seconds, text, onClose }: TimerOverlayProps) {
  const [left, setLeft] = useState(seconds);
  const done = useRef(false);

  useEffect(() => {
    // Отсчёт по абсолютному времени, а не «минус секунда за тик»: вкладку в
    // Telegram усыпляют, и накопленный дрейф превратил бы минуту в полторы.
    const until = Date.now() + seconds * 1000;
    const id = setInterval(() => {
      const rest = Math.max(0, Math.round((until - Date.now()) / 1000));
      setLeft(rest);
      if (rest === 0 && !done.current) {
        done.current = true;
        notify('warning');
        clearInterval(id);
      }
    }, 250);
    return () => clearInterval(id);
  }, [seconds]);

  return (
    <motion.div
      className="fixed inset-0 z-20 flex flex-col justify-between bg-veil px-5"
      style={{ paddingTop: 'var(--clown-safe-top)', paddingBottom: 'var(--clown-safe-bottom)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.16 }}
    >
      {/* Задание читают по ходу отсчёта — длинный текст обязан прокручиваться,
          а не уезжать под кнопку «стоп». */}
      <div className="overflow-y-auto pt-8">
        <p className="text-meta uppercase tracking-widest text-mute">
          {left === 0 ? 'Время вышло' : 'Осталось'}
        </p>
        <p className="font-display text-clock text-acc">{left}</p>

        <div className="mt-4 h-gauge w-full bg-off">
          <motion.div
            className="h-full bg-acc"
            initial={false}
            animate={{ width: `${(left / Math.max(1, seconds)) * 100}%` }}
            transition={{ duration: 0.25, ease: 'linear' }}
          />
        </div>

        <p className="mt-6 text-lead text-dim">{text}</p>
      </div>

      <button
        type="button"
        onClick={() => {
          impact('light');
          onClose();
        }}
        className="flex h-tap w-full items-center justify-center border-2 border-line text-meta uppercase tracking-widest text-ink"
      >
        {left === 0 ? 'Закрыть' : 'Стоп'}
      </button>
    </motion.div>
  );
}
